import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import authService from "../services/authService";
import Cookies from "js-cookie";
import "../css/Login.css";
import { auth, googleProvider } from "../config/firebase";
import { signInWithPopup, signInWithEmailAndPassword } from "firebase/auth";
import { getBaseUrl } from "../utils/apiHelper";

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const [showReset, setShowReset] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            if (!userCredential.user.emailVerified) {
                setMessage("Please verify your email before logging in. Check your inbox for a verification email.");
                setMessageType("info");
                return;
            }
        } catch (firebaseError) {
            // Ignore Firebase error if user is not in Firebase, fallback to backend
        }
        try {
            await authService.login(email, password);
            const userId = authService.getUserId();
            const username = Cookies.get("username");
            const token = Cookies.get("token");
            const refreshToken = Cookies.get("refreshToken");

            if (!userId) {
                console.error("Failed to retrieve userId from cookies after login.");
                setMessage("Login failed: Unable to retrieve user ID.");
                setMessageType("error");
                return;
            }

            if (!token && !refreshToken) {
                console.error("Failed to retrieve authentication tokens after login.");
                setMessage("Login failed: Unable to retrieve authentication tokens.");
                setMessageType("error");
                return;
            }

            console.log("Logged in user ID:", userId);
            console.log("Logged in username (email):", username);
            console.log("Access token available:", !!token);
            console.log("Refresh token available:", !!refreshToken);

            navigate("/members");
        } catch (error) {
            console.error("Error during login:", error);
            setMessage(error.message);
            setMessageType("error");
        }
    };

    const handleFirebaseGoogleLogin = async () => {
        try {
            setMessage("Connecting to Google...");
            setMessageType("info");
            console.log("[Google Login] Firebase config:", {
                apiKey: auth.app.options.apiKey,
                authDomain: auth.app.options.authDomain,
                projectId: auth.app.options.projectId,
                appId: auth.app.options.appId,
            });
            console.log("[Google Login] Starting signInWithPopup...");
            const result = await signInWithPopup(auth, googleProvider);
            console.log("[Google Login] signInWithPopup result:", result);
            const user = result.user;
            console.log("[Google Login] User:", user);
            setMessage("Google authentication successful, verifying with server...");
            const idToken = await user.getIdToken();
            console.log("[Google Login] ID Token:", idToken);
            const authResult = await authService.handleFirebaseGoogleAuth({
                idToken,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL
            });
            console.log("[Google Login] Backend auth result:", authResult);
            navigate("/members");
        } catch (error) {
            console.error("[Google Login] Error during Google login:", error);
            if (error && error.stack) {
                console.error("[Google Login] Error stack:", error.stack);
            }
            setMessage(`Google login failed: ${error.message || error}`);
            setMessageType("error");
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${getBaseUrl()}/api/auth/send-reset-code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: resetEmail })
            });
            const data = await res.json();
            if (res.ok) {
                setMessage("Password reset email sent! Check your inbox.");
                setMessageType("success");
                setShowReset(false);
            } else {
                setMessage(data.message || "Failed to send reset email.");
                setMessageType("error");
            }
        } catch (error) {
            setMessage(`Failed to send reset email: ${error.message}`);
            setMessageType("error");
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                <form onSubmit={handleLogin} className="form-container">
                    <div className="auth-logo">
                        📊
                    </div>
                    <h2>Welcome Back</h2>
                    <p className="auth-subtitle">Sign in to continue to Autofolio</p>

                    <div className="input-group has-label">
                        <label htmlFor="email">Email Address</label>
                        <span className="input-icon">📧</span>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="Enter your email"
                        />
                    </div>

                    <div className="input-group has-label">
                        <label htmlFor="password">Password</label>
                        <span className="input-icon">🔒</span>
                        <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="Enter your password"
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            tabIndex={-1}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            onClick={() => setShowPassword(v => !v)}
                        >
                            {showPassword ? "👁️" : "👁️‍🗨️"}
                        </button>
                    </div>

                    <button type="submit" className="submit-btn">
                        Sign In
                    </button>

                    <button
                        type="button"
                        className="forgot-password-btn"
                        onClick={() => setShowReset(!showReset)}
                    >
                        Forgot Password?
                    </button>

                    {showReset && (
                        <div className="reset-section">
                            <div className="input-group">
                                <span className="input-icon">📧</span>
                                <input
                                    type="email"
                                    value={resetEmail}
                                    onChange={e => setResetEmail(e.target.value)}
                                    placeholder="Enter your email"
                                />
                            </div>
                            <button type="button" className="reset-btn" onClick={handleForgotPassword}>
                                Send Reset Email
                            </button>
                        </div>
                    )}

                    <div className="separator">
                        <span>OR</span>
                    </div>

                    <button
                        type="button"
                        onClick={handleFirebaseGoogleLogin}
                        className="google-auth-btn"
                    >
                        Sign in with Google
                    </button>

                    {message && (
                        <p className={
                            messageType === "error" ? "error-message" :
                            messageType === "success" ? "success-message" :
                            "info-message"
                        }>
                            {message}
                        </p>
                    )}

                    <div className="auth-footer">
                        Don't have an account? <Link to="/register">Create one</Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;
